import { z } from "zod";
import { create } from "zustand";
import type {
  ShopProductDetail,
  ShopProductVariant,
} from "../pages/shop/catalogue";
import {
  fetchShopProductById,
  fetchShopProductsByIds,
} from "../pages/shop/shop.service";

/**
 * A cart entry ready to use. Only `productId`, `variantId` and `quantity` are
 * the buyer's own choice and reach storage; the rest is catalogue data added
 * back on when the cart is loaded, so the cart never holds a name or a price
 * of its own to go stale.
 */
export type ShopCartItem = {
  productId: string;
  variantId: string;
  quantity: number;
  productName: string;
  variantLabel: string;
  unitPriceCents: number;
  imageUrl: string;
  /** How many of this variant the catalogue had when the cart was loaded. */
  maxQuantity: number;
};

const persistedShopCartItemSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  quantity: z.number().int().positive(),
});

const persistedShopCartSchema = z.object({
  version: z.literal(1),
  expiresAt: z.string().refine((value) => {
    return !Number.isNaN(new Date(value).getTime());
  }),
  items: z.array(persistedShopCartItemSchema).min(1),
});

type PersistedShopCartItem = z.infer<typeof persistedShopCartItemSchema>;
type PersistedShopCart = z.infer<typeof persistedShopCartSchema>;

export const SHOP_CART_STORAGE_KEY = "co2-shop-cart-v1";
export const SHOP_CART_TTL_MS = 24 * 60 * 60 * 1000;

const addShopCartItem = (
  existingItems: ShopCartItem[],
  newItem: ShopCartItem,
) => {
  if (newItem.quantity <= 0) {
    throw new Error("Shop cart items require a positive quantity");
  }

  const existingItemIndex = existingItems.findIndex((existingItem) => {
    return isSameCartItem(existingItem, newItem.productId, newItem.variantId);
  });

  if (existingItemIndex === -1) {
    return [...existingItems, newItem];
  }

  return existingItems.map((existingItem, index) => {
    if (index !== existingItemIndex) {
      return existingItem;
    }

    return {
      ...existingItem,
      quantity: existingItem.quantity + newItem.quantity,
    };
  });
};

const isSameCartItem = (
  item: ShopCartItem,
  productId: string,
  variantId: string,
) => {
  return item.productId === productId && item.variantId === variantId;
};

const removeShopCartItem = (
  items: ShopCartItem[],
  productId: string,
  variantId: string,
) => {
  return items.filter((item) => {
    return !isSameCartItem(item, productId, variantId);
  });
};

/**
 * Sets the quantity of a single cart item. A quantity that is no longer
 * positive means the buyer emptied it, so it leaves the cart.
 */
const setShopCartItemQuantity = (
  items: ShopCartItem[],
  productId: string,
  variantId: string,
  quantity: number,
) => {
  if (quantity <= 0) {
    return removeShopCartItem(items, productId, variantId);
  }

  return items.map((item) => {
    if (!isSameCartItem(item, productId, variantId)) {
      return item;
    }

    return {
      ...item,
      quantity,
    };
  });
};

export const getShopCartTotalCents = (items: ShopCartItem[]) => {
  return items.reduce((total, item) => {
    return total + item.unitPriceCents * item.quantity;
  }, 0);
};

export const getShopCartQuantity = (items: ShopCartItem[]) => {
  return items.reduce((total, item) => {
    return total + item.quantity;
  }, 0);
};

export const buildPersistedShopCart = (
  items: ShopCartItem[],
  now: Date,
  expiresAt = new Date(now.getTime() + SHOP_CART_TTL_MS).toISOString(),
): PersistedShopCart => {
  return {
    version: 1,
    expiresAt,
    items: items.map((item) => {
      return {
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
      };
    }),
  };
};

export const readPersistedShopCart = (rawValue: string | null) => {
  if (!rawValue) {
    return null;
  }

  try {
    return persistedShopCartSchema.parse(JSON.parse(rawValue));
  } catch (error) {
    console.warn("Invalid persisted shop cart payload. Clearing storage.", error);
    return null;
  }
};

/**
 * The one place catalogue data becomes cart data, used both when an item
 * enters the cart and when a stored one is loaded back.
 */
const toCartItem = (
  product: ShopProductDetail,
  variant: ShopProductVariant,
  quantity: number,
): ShopCartItem => {
  return {
    productId: product.id,
    variantId: variant.id,
    quantity,
    productName: product.name,
    variantLabel: variant.label,
    unitPriceCents: product.priceCents,
    imageUrl: product.mainImageUrl,
    maxQuantity: variant.availableQuantity,
  };
};

/**
 * Adds the catalogue data back on to what storage kept. An entry whose product
 * or variant is gone, or sold out, cannot be bought and so does not come back;
 * one that outgrew the remaining stock comes back trimmed to it. Restoring is
 * silent: a cart that shrank simply returns smaller.
 */
export const restoreShopCartItems = (
  persistedItems: PersistedShopCartItem[],
  products: ShopProductDetail[],
) => {
  const productsById = new Map(
    products.map((product) => {
      return [product.id, product];
    }),
  );

  return persistedItems.reduce<ShopCartItem[]>((items, persistedItem) => {
    const product = productsById.get(persistedItem.productId);
    const variant = product?.variants.find((candidate) => {
      return candidate.id === persistedItem.variantId;
    });

    // A sold out product is one whose every variant is sold out, so the
    // variant check already covers it.
    if (!product || !variant || variant.isSoldOut) {
      return items;
    }

    return addShopCartItem(
      items,
      toCartItem(
        product,
        variant,
        Math.min(persistedItem.quantity, variant.availableQuantity),
      ),
    );
  }, []);
};

const isBrowser = () => {
  return typeof window !== "undefined";
};

const isExpired = (expiresAt: string, now: Date) => {
  return new Date(expiresAt).getTime() <= now.getTime();
};

const removeStoredShopCart = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(SHOP_CART_STORAGE_KEY);
};

const writeStoredShopCart = (
  items: ShopCartItem[],
  now: Date,
  expiresAt?: string,
) => {
  if (!isBrowser()) {
    return;
  }

  if (items.length === 0) {
    removeStoredShopCart();
    return;
  }

  const payload = buildPersistedShopCart(items, now, expiresAt);
  window.localStorage.setItem(SHOP_CART_STORAGE_KEY, JSON.stringify(payload));
};

type ShopCartState = {
  items: ShopCartItem[];
  /** True while the catalogue data is being added back on to the stored cart. */
  isRestoring: boolean;
  initialize: () => Promise<void>;
  syncFromStorage: () => Promise<void>;
  /**
   * Sets the final quantity for one cart item, creating it from live catalogue
   * data when needed and removing it when the quantity reaches zero.
   */
  setCartItem: (
    productId: string,
    variantId: string,
    quantity: number,
  ) => Promise<void>;
  clear: () => void;
};

export const useShopCartStore = create<ShopCartState>((set) => {
  const emptyCart = { items: [], isRestoring: false };

  const hydrateFromStorage = async () => {
    if (!isBrowser()) {
      set(emptyCart);
      return;
    }

    set({ isRestoring: true });

    try {
      const rawValue = window.localStorage.getItem(SHOP_CART_STORAGE_KEY);

      if (!rawValue) {
        set(emptyCart);
        return;
      }

      const persistedCart = readPersistedShopCart(rawValue);

      if (!persistedCart) {
        removeStoredShopCart();
        set(emptyCart);
        return;
      }

      if (isExpired(persistedCart.expiresAt, new Date())) {
        removeStoredShopCart();
        set(emptyCart);
        return;
      }

      const productIds = [...new Set(persistedCart.items.map((item) => item.productId))];
      const products = await fetchShopProductsByIds(productIds);
      const restoredItems = restoreShopCartItems(persistedCart.items, products);

      if (restoredItems.length === 0) {
        removeStoredShopCart();
        set(emptyCart);
        return;
      }

      writeStoredShopCart(restoredItems, new Date(), persistedCart.expiresAt);
      set({ items: restoredItems, isRestoring: false });
    } catch (error) {
      console.error("Error restoring persisted shop cart:", error);
      set(emptyCart);
    }
  };

  // Every mutation rewrites storage from the resulting cart, so what is in
  // memory and what is stored never drift apart.
  const persist = (items: ShopCartItem[]) => {
    writeStoredShopCart(items, new Date());

    return { items };
  };

  return {
    items: [],
    // The cart starts out unknown: the stored one has not been read yet.
    isRestoring: true,
    initialize: async () => {
      await hydrateFromStorage();
    },
    syncFromStorage: async () => {
      await hydrateFromStorage();
    },
    setCartItem: async (productId, variantId, quantity) => {
      if (quantity <= 0) {
        set((state) => {
          return persist(
            setShopCartItemQuantity(state.items, productId, variantId, quantity),
          );
        });
        return;
      }

      const product = await fetchShopProductById(productId);
      const variant = product?.variants.find((candidate) => {
        return candidate.id === variantId;
      });

      if (!product || !variant || variant.isSoldOut) {
        throw new Error("Questo articolo non è più disponibile");
      }

      set((state) => {
        return persist(
          setShopCartItemQuantity(
            addShopCartItem(state.items, toCartItem(product, variant, quantity)),
            productId,
            variantId,
            quantity,
          ),
        );
      });
    },
    clear: () => {
      removeStoredShopCart();
      set(emptyCart);
    },
  };
});
